import { useState, useEffect, useMemo, useRef } from "react";
import { FormikValues, useFormik } from "formik";
import { ButtonGroup } from "@allaria-tech/matter/ButtonGroup";
import "../../App.css";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { isDigital } from "../../app/store";
import {
  onUpdateStep,
  onUpdateSubStep,
  onUpdateFormData,
  onUpdateDraftUUID,
  onUpdateExpiredSessionResume,
} from "../../reducers/onboardingDataReducer";
import { onUpdateFormData as onUpdateSecuritiesFormData } from "../../reducers/securitiesReducer";
import { Field, Step } from "../../types";
import FormContainer from "../FormContainer/FormContainer";
import Container from "../Container/Container";
import Layout from "../Layout/Layout";
import { useNavigate, useSearchParams } from "react-router-dom";
import TemplateInterpreterForm from "./TemplateInterpreterForm";
import TemplateInterpreterSecondaryForm from "./TemplateInterpreterSecondaryForm";
import {
  onUpdateIsNavigating,
  onUpdatePrimaryButton,
  onUpdateSecondaryButton,
  onUpdateTertiaryButton,
} from "../../reducers/buttonsReducer";
import {
  getDniFromCuit,
  getResumeReferralValues,
  isAllowedRedirectUrl,
  isAllariaFondosOnboarding,
  isBasicStep,
  isComponentStep,
  isJuridicalOnboarding,
  isUnifiedOnboarding,
  loadFavicon,
} from "../../utils";
import { LoadingCircle } from "@allaria-tech/matter/LoadingCircle";
import PEPModal from "../PEPModal/PEPModal";
import QualifiedInvestorModal from "../QualifiedInvestorModal/QualifiedInvestorModal";
import TramitNumberModal from "../TramitNumberModal/TramitNumberModal";
import RiskProfileModal from "../RiskProfileModal/RiskProfileModal";
import RiskProfileModalSecurities from "../RiskProfileModalSecurities/RiskProfileModal";
import { onUpdateInfoStep } from "../../reducers/infoStepReducer";
import { getDraft } from "../../connectors/connectors";
import { store } from "../../app/store";
import * as Sentry from "@sentry/react";
import {
  getPartnership,
  getReferrerPartnershipKey,
} from "../../utils/companies";
import {
  generateYupSchema,
  getKeys,
  getMissingFieldInitialValues,
  getOptionalFieldNames,
  getRequiredFieldNames,
  getValue,
  isEmptyObject,
  mergeDeep,
  reorderFields,
  setNestedValue,
} from "./utils";
import useForm from "../../hooks/useForm";
import useWindowWidth from "../../hooks/useWindowWidth";
import { logAnalyticsAndHotjarEvent } from "../../main";
import EditDNIModal from "../EditDNIModal/EditDNIModal";
import AccountObjectiveModal from "../AccountObjectiveModal/AccountObjectiveModal";
import {
  primaryButtonText,
  secondaryButtonText,
  tertiaryButtonText,
} from "../../i18n/interpreter";
import SupplementaryComponentInfoRenderer from "../SupplementaryComponentInfoRenderer/SupplementaryComponentInfoRenderer";
import { authorizeDraft, canRenewExpiredToken, isDev } from "../../app/utils";
import useTemplateInterpreter from "../../hooks/useTemplateInterpreter";
import useRemoteParams from "../../hooks/useRemoteParams";
import useCryptoGuard from "../../hooks/useCryptoGuard";
import useCryptoBridge from "../../hooks/useCryptoBridge";
import useMobileLayout from "../../hooks/useMobileLayout";
import { updateToken } from "../../reducers/authReducer";

import styles from "./TemplateInterpreter.module.scss";
import { onUpdateTheme } from "../../reducers/activeTemplateReducer";

const TemplateInterpreter = () => {
  const {
    loading: loadingStyle,
    buttonsGroup,
    cryptoDesktopButtons,
    headerFormContainer,
    component,
    secondaryFormTemplate,
    templateContainer,
    form,
    flexColumn,
  } = styles;

  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const preload = searchParams.get("preload") === "true";
  const preloadBootRef = useRef(preload);

  const [loading, setLoading] = useState({
    template: true,
    formValues: true,
    onLoad: preload,
  });

  const state = useAppSelector((state) => state);

  const language = state.securities.language ?? "es";

  const { width } = useWindowWidth();
  const isMobile = width < 768;
  // In the app's desktop modal the frame is 560px wide: its buttons stay
  // desktop, with no mobile container.
  const { isCrypto, isMobile: isCryptoMobile } = useMobileLayout();

  const {
    activeTemplate: template,
    buttons: {
      primaryButtonData,
      secondaryButtonData,
      tertiaryButtonData,
      isNavigating,
    },
    onboardingData: {
      stepData: { step, subStep, reachedSteps },
    },
    infoStep: { type: infoStepType },
    preconfirmation: { isEditingForm },
    securities,
  } = state;

  const {
    values: { email, token, uuid, cuit },
  } = useRemoteParams();

  // Sends the user to Allaria Digital when the crypto onboarding is opened
  // outside the Allaria app, or without the data it needs to run.
  useCryptoGuard({ onboardingType: template?.onboardingType, token });

  const isLoading = loading.template || loading.formValues || loading.onLoad;

  // Keeps the token channel with the Allaria app open for the whole flow.
  useCryptoBridge({ onboardingType: template?.onboardingType, isLoading });

  // Email or name of the person that referred the user
  const referrer = decodeURIComponent(searchParams.get("ref") || "").replace(
    / /g,
    "+",
  );
  // Get the email from the URL
  const emailByUrl = searchParams.get("accountEmail")?.replace(/ /g, "+"); // Prevent spaces in email from breaking the URL
  const cuitByUrl = searchParams.get("accountCuit");
  // Company CUIT for JURIDICAL resumes (e.g. the email-verification link).
  const businessCuitByUrl = searchParams.get("businessCuit");

  // Validate URL params if the onboarding type requires them.
  useEffect(() => {
    if (!template?.onboardingType) return;

    const redirectUrl = searchParams.get("redirect_url");
    const partnership = getPartnership();
    const allowedDomains = partnership?.successRedirect?.allowedDomains ?? [];
    const isInvalid =
      (!!redirectUrl && !isAllowedRedirectUrl(redirectUrl, allowedDomains)) ||
      (cuit && cuit.length !== 11);

    if (isInvalid) {
      if (infoStepType === "INVALID_PARAMS") return;
      Sentry.captureException(
        new Error("Invalid redirect_url: blocked onboarding start"),
        {
          extra: {
            redirectUrl,
            partnershipKey: partnership?.key ?? null,
            allowedDomains,
          },
          tags: { security: "invalid_redirect_url" },
        },
      );
      dispatch(onUpdateInfoStep("INVALID_PARAMS"));
      dispatch(onUpdateStep("information-step"));
    } else if (infoStepType === "INVALID_PARAMS") {
      dispatch(onUpdateInfoStep(null));
      dispatch(onUpdateStep(1));
    }
  }, [template?.onboardingType, infoStepType, searchParams, cuit]);

  const uuidByUrl = searchParams.get("uuid");
  const persistedDraftUUID = state.onboardingData.draftUUID;
  const staleDraftWipedRef = useRef(false);

  useEffect(() => {
    if (
      staleDraftWipedRef.current ||
      !uuidByUrl ||
      !persistedDraftUUID ||
      uuidByUrl === persistedDraftUUID
    ) {
      return;
    }
    staleDraftWipedRef.current = true;
    dispatch({ type: "reset-store" });
  }, [uuidByUrl, persistedDraftUUID, dispatch]);

  useEffect(() => {
    if (token) dispatch(updateToken(token));
    dispatch({ type: "update-token", payload: token });

    if (uuid) {
      dispatch(onUpdateDraftUUID(uuid));
    }

    if (email && cuit) {
      dispatch(
        onUpdateFormData({
          email,
          cuit,
          identityDocument: getDniFromCuit(cuit),
        }),
      );
    }

    if (businessCuitByUrl) {
      dispatch(
        onUpdateFormData({
          businessInformation: { cuit: businessCuitByUrl },
        }),
      );
    }
  }, [token, uuid, email, cuit, businessCuitByUrl]);

  // Redirects the user based on the configured company
  // - If the company is "ALLARIA_SECURITY", redirect from "/personal" or "/empresarial" to "/securities"
  // - If the company is "ALLARIAFONDOS", redirect from "/personal" to "/empresarial"
  // - If the company is different, redirect from "/securities" to "/personal"
  useEffect(() => {
    const company = import.meta.env.VITE_COMPANY;

    const { pathname } = window.location;

    const isAllariaSecurity = company === "ALLARIA_SECURITY";
    const isAllariaFondos = isAllariaFondosOnboarding();

    const shouldRedirectToSecurities =
      isAllariaSecurity && ["/personal", "/empresarial"].includes(pathname);
    const shouldRedirectToPersonal =
      !isAllariaSecurity && pathname === "/securities";
    const shouldRedirectToEmpresarial =
      isAllariaFondos && pathname === "/personal";

    if (shouldRedirectToSecurities) navigate("/securities");
    else if (shouldRedirectToPersonal) navigate("/personal");
    else if (shouldRedirectToEmpresarial) navigate("/empresarial");
  }, []);

  useEffect(() => {
    if (template.onboardingType) return;
    redirectToOnboarding();
  }, [template]);

  // Load template
  // Set favicon
  useEffect(() => {
    if (!template?.onboardingType) return;

    if (!template.formTitle) {
      loadTemplate();
    } else if (template.theme) {
      setLoading((prevState) => ({
        ...prevState,
        template: false,
      }));
    }

    if (template?.favicon) {
      loadFavicon(template.favicon);
    }
  }, [template]);

  useEffect(() => {
    if (
      template?.onboardingType === "SECURITIES" &&
      state.securities.language
    ) {
      loadTemplate();
    }
  }, [
    state.securities.language,
    state.securities.accountBeneficiaries,
    state.securities.beneficiariesCount,
    state.securities.currentHolder,
    state.securities.accountHolders,
    state.securities.draftUUID,
    state.securities.holdersData,
    state.securities.formValues,
    template.onboardingType,
  ]);

  // Flatten step fields into a single array
  const flattenStepFields = useMemo(
    () =>
      template?.steps?.reduce(
        (acc: Field[], { fields, subSteps, secondaryForm }) => {
          if (subSteps) {
            subSteps.forEach(({ fields, secondaryForm }: Step) => {
              if (fields && !secondaryForm) acc.push(...fields);
            });
          } else {
            if (fields && !secondaryForm) acc.push(...fields);
          }
          return acc;
        },
        [],
      ),
    [template?.steps],
  );

  // Flatten step fields initial values
  const flattenStepFieldsInitialValues = useMemo(
    () =>
      flattenStepFields?.reduce((acc: FormikValues, { name, initialValue }) => {
        setNestedValue(acc, name, initialValue);
        return acc;
      }, {}),
    [flattenStepFields],
  );

  // Generate Yup schema for form validation
  const flattenStepFieldsSchema = generateYupSchema(flattenStepFields, state);

  // Set initial values for formik
  const initialValues = flattenStepFieldsInitialValues;

  // Formik
  const formik = useFormik({
    initialValues: initialValues || {},
    validationSchema: flattenStepFieldsSchema,
    onSubmit: (_, { setSubmitting }) => {
      setSubmitting(false);
    },
  });

  const {
    errors,
    touched,
    values,
    handleSubmit,
    isSubmitting,
    setValues,
    resetForm,
  } = formik;

  const { currentStep, currentStepData, handleBack, handleContinue } =
    useForm(formik);

  const { loadTemplate, formValues, draftUUID, redirectToOnboarding } =
    useTemplateInterpreter({
      dispatch,
      onboardingType: template.onboardingType,
      values,
      state,
      draftUUID: uuid,
    });

  // Run the template's onLoad handler when a preload was requested (e.g. the
  // email-verification link resume). EmailVerificationPage lives outside
  // TemplateInterpreter (no Formik), so the authorize/success logic runs here,
  // only once, and only if the template defines onLoad.
  const preloadStartedRef = useRef(false);

  useEffect(() => {
    if (!preload || preloadStartedRef.current) return;
    if (loading.template || loading.formValues || !draftUUID) return;

    preloadStartedRef.current = true;

    // `infoStep` is persisted, so a resume boot must not inherit the previous
    // session's reason (e.g. PENDING_REGISTRY_LOCAL) — onLoad routes on it.
    dispatch(onUpdateInfoStep(null));

    const runOnLoad = async (onLoadState: typeof state) => {
      await template.onLoad?.({
        draftUUID,
        dispatch,
        formik,
        state: onLoadState,
      });
    };

    authorizeDraft(
      () => runOnLoad(state),
      async () => {
        dispatch(onUpdateInfoStep(null));
        await runOnLoad(store.getState());
      },
    ).finally(() => {
      setLoading((prevState) => ({
        ...prevState,
        onLoad: false,
      }));
      // The resume params were only needed to seed redux on this fresh boot;
      // they now live in the persisted store, so drop them from the URL. `ref`
      // stays — the PENDING_REGISTRY_LOCAL partnership check reads it on reload.
      const nextParams = new URLSearchParams(window.location.search);
      ["preload", "token", "uuid", "email", "cuit", "businessCuit"].forEach(
        (param) => nextParams.delete(param),
      );
      setSearchParams(nextParams, { replace: true });
    });
  }, [
    preload,
    loading.template,
    loading.formValues,
    draftUUID,
    template.onboardingType,
  ]);

  // A malformed resume link (no `uuid`/`token`, so no draft to load) leaves the
  // effect above unable to run, and it is the only thing that clears
  // `loading.onLoad` — without this the spinner never goes away. Give up once
  // everything else is ready and let the user start at step 1 instead.
  useEffect(() => {
    if (!preload || preloadStartedRef.current) return;
    if (loading.template || loading.formValues || draftUUID) return;

    setLoading((prevState) => ({
      ...prevState,
      onLoad: false,
    }));
  }, [preload, loading.template, loading.formValues, draftUUID]);

  // Set initial form values dynamically
  useEffect(() => {
    // A template without fields (e.g. Allaria Digital's missing-draft error) has
    // nothing to set: dispatching `{}` still builds a new formValues, which
    // re-runs this effect forever.
    if (
      !flattenStepFieldsInitialValues ||
      isEmptyObject(flattenStepFieldsInitialValues)
    )
      return;

    if (
      (flattenStepFieldsInitialValues && !formValues) ||
      (formValues && isEmptyObject(formValues))
    ) {
      flattenStepFieldsInitialValues &&
        dispatch(
          template?.onboardingType === "SECURITIES"
            ? onUpdateSecuritiesFormData(flattenStepFieldsInitialValues)
            : onUpdateFormData(flattenStepFieldsInitialValues),
        );
    }
  }, [flattenStepFieldsInitialValues, dispatch, formValues]);

  // Backfill defaults when the template loads after formValues were already merged
  // (e.g. email/cuit from URL). Only fills fields that are still empty.
  useEffect(() => {
    if (
      !flattenStepFields?.length ||
      !flattenStepFieldsInitialValues ||
      isEmptyObject(flattenStepFieldsInitialValues)
    ) {
      return;
    }

    setValues((prevValues) => {
      const missingDefaults = getMissingFieldInitialValues(
        prevValues,
        flattenStepFields,
      );

      if (isEmptyObject(missingDefaults)) return prevValues;

      dispatch(
        template?.onboardingType === "SECURITIES"
          ? onUpdateSecuritiesFormData(missingDefaults)
          : onUpdateFormData(missingDefaults),
      );

      return mergeDeep(prevValues, missingDefaults);
    }, false);
  }, [
    flattenStepFields,
    flattenStepFieldsInitialValues,
    setValues,
    dispatch,
    template?.onboardingType,
  ]);

  const [initialFormDataLoaded, setInitialFormDataLoaded] = useState(false);

  // Check draft availability
  useEffect(() => {
    const checkDraftAvailability = async () => {
      try {
        await getDraft({ draftUUID });
        return true;
      } catch (error) {
        return false;
      }
    };

    const checkDraft = async () => {
      if (
        !initialFormDataLoaded &&
        loading?.formValues &&
        formValues &&
        !isEmptyObject(formValues)
      ) {
        let formValuesDefault = formValues;
        if (!referrer && formValuesDefault?.referrer && !reachedSteps) {
          formValuesDefault = {
            ...formValuesDefault,
            referrer: "",
            referredBool: "",
          };
        }

        setValues((prevValues) => {
          return {
            ...flattenStepFieldsInitialValues,
            ...prevValues,
            ...formValuesDefault,
          };
        }, false);

        setLoading((prevState) => ({
          ...prevState,
          formValues: false,
        }));

        setInitialFormDataLoaded(true);

        if (preloadBootRef.current) return;

        if (
          reachedSteps &&
          reachedSteps.find((step) => step > 2) &&
          !isEditingForm
        ) {
          try {
            //An expired token can't read the draft, which is still there:
            //resuming asks for the email again. Signatories and coowners
            //request the code with the draftUUID, so they start over
            const draftAvailable =
              canRenewExpiredToken() || (await checkDraftAvailability());

            //Only a change of partnership matters: a referrer typed in step 1
            //is never in the URL
            const partnershipMismatch =
              getReferrerPartnershipKey(formValuesDefault?.referrer) !==
              getReferrerPartnershipKey(referrer);

            if (draftAvailable && !partnershipMismatch) {
              const draftReferrer =
                store.getState().authentication.draftBase?.referrer;
              if (draftReferrer) {
                const referralValues = getResumeReferralValues(
                  draftReferrer,
                  formValuesDefault,
                );
                setValues(
                  (prevValues) => ({ ...prevValues, ...referralValues }),
                  false,
                );
                dispatch(onUpdateFormData(referralValues));
              }
              dispatch(onUpdateInfoStep("PENDING_REGISTRY_LOCAL"));
              dispatch(onUpdateStep("information-step"));
            } else {
              resetForm();
              dispatch({ type: "reset-store" });
              dispatch(onUpdateStep(1));
            }
          } catch {
            resetForm();
            dispatch({ type: "reset-store" });
            dispatch(onUpdateStep(1));
          }
        } else if (reachedSteps && !reachedSteps.find((step) => step === 3)) {
          resetForm();
          dispatch({ type: "reset-store" });
          dispatch(onUpdateStep(1));
        }
      }
    };

    template?.onboardingType &&
      template?.onboardingType !== "SECURITIES" &&
      checkDraft();
  }, [
    loading.formValues,
    formValues,
    initialFormDataLoaded,
    template?.onboardingType,
  ]);

  useEffect(() => {
    if (!loading.formValues || loading.template) return;
    if (formValues && !isEmptyObject(formValues)) return;
    if (
      flattenStepFieldsInitialValues &&
      !isEmptyObject(flattenStepFieldsInitialValues)
    ) {
      return;
    }

    setLoading((prevState) => ({
      ...prevState,
      formValues: false,
    }));
  }, [
    loading.formValues,
    loading.template,
    formValues,
    flattenStepFieldsInitialValues,
  ]);

  useEffect(() => {
    // reset securities form
    if (template?.onboardingType === "SECURITIES") {
      if (loading?.formValues && formValues && !isEmptyObject(formValues)) {
        setValues(
          (prevValues) => ({
            ...prevValues,
            ...formValues,
          }),
          false,
        );
        setLoading((prevState) => ({
          ...prevState,
          formValues: false,
        }));
      }
    }
  }, [template.onboardingType, formValues, loading.formValues]);

  // Get required fields names from current step
  const currentStepRequiredFieldsNames = useMemo(() => {
    if (!isBasicStep(currentStepData)) return [];

    if ("fields" in currentStepData && currentStepData.fields) {
      return getRequiredFieldNames(
        currentStepData.fields,
        state,
        values,
        dispatch,
      );
    } else {
      return [];
    }
  }, [currentStepData, values]);

  const currentStepOptionalFieldsName = useMemo(() => {
    if (!isBasicStep(currentStepData)) return [];

    if ("fields" in currentStepData && currentStepData.fields) {
      return getOptionalFieldNames(
        currentStepData.fields,
        state,
        values,
        dispatch,
      );
    } else {
      return [];
    }
  }, [currentStepData, values]);

  const primaryButtonDisabled = () => {
    // Current Step Required Fields Have Errors
    // Current Step Required Fields Are Touched
    // Current Step Required Fields Are Not Empty

    return (
      (getKeys(errors).filter(
        (key) =>
          currentStepRequiredFieldsNames?.includes(key) ||
          (currentStepOptionalFieldsName.includes(key) &&
            !!getValue(values, key)),
      ).length > 0 &&
        getKeys(touched).filter(
          (key) =>
            currentStepRequiredFieldsNames?.includes(key) ||
            currentStepOptionalFieldsName.includes(key),
        ).length > 0) ||
      !currentStepRequiredFieldsNames
        ?.map((name) => !!getValue(values, name))
        .every((value) => value) ||
      isSubmitting
    );
  };

  // Update buttons when current step changes and when formik values change
  useEffect(() => {
    if (!currentStepData) return;

    if (!("component" in currentStepData)) {
      dispatch(
        onUpdatePrimaryButton({
          text: primaryButtonText[language],
          disabled: primaryButtonDisabled(),
          isLoading: false,
          type: "button",
          onClick: handleContinue,
          show: true,
        }),
      );
      //Check if user came from the selector proyect
      const previousPageUrl = document.referrer?.includes(
        "seleccion-cuenta.allaria",
      );

      const onClickHandler =
        previousPageUrl && step === 1
          ? () => window.history.back()
          : handleBack;

      const secondaryButtonDisabled =
        currentStepData?.disableButtonBack ||
        (currentStepData?.step === 1 && currentStep?.disableButtonBack);

      const showButton =
        currentStep?.step === 1 ? !!previousPageUrl : !secondaryButtonDisabled;

      const { secondaryButton } = currentStepData;

      dispatch(
        onUpdateSecondaryButton({
          text: secondaryButton?.text ?? secondaryButtonText[language],
          disabled: false,
          isLoading: false,
          onClick: secondaryButton?.onClick ?? onClickHandler,
          show: !!secondaryButton || showButton,
        }),
      );

      dispatch(
        onUpdateTertiaryButton({
          text: tertiaryButtonText[language],
          disabled: false,
          isLoading: false,
          onClick: handleBack,
          show: false,
        }),
      );
    }
  }, [
    currentStepData,
    values,
    errors,
    touched,
    isSubmitting,
    currentStep?.step,
  ]);

  // Reorder fields
  useEffect(() => {
    if (!currentStepData || !("fields" in currentStepData)) return;

    reorderFields(currentStepData, values, setValues);
  }, [values]);

  useEffect(() => {
    const handleBeforeUnload = () =>
      logAnalyticsAndHotjarEvent("onboarding_abandoned", { step });

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    // We iterate over all fields with errors
    Object.keys(errors).forEach((field) => {
      if (touched[field] && errors[field]) {
        // If the field has been touched and has an error, we trigger the event
        logAnalyticsAndHotjarEvent("form_error", {
          field,
          error: errors[field],
        });
      }
    });
  }, [errors, touched]);

  // The referrer reset below drops a stale persisted referrer on a fresh boot
  // without `ref`, so it must run only once per boot: the effect also re-runs on
  // every step change, and without this flag it would wipe a referrer the user
  // typed by hand as soon as they navigated back to step 1.
  const referrerResetRef = useRef(false);

  // Add referrer to form values if found in the URL
  useEffect(() => {
    if (template?.onboardingType === "SECURITIES") return;
    if (referrer) {
      const isSav3Ref = referrer.toLowerCase().startsWith("sav3");
      const newValues = {
        referredBool: "SI",
        referrer: referrer.includes("@")
          ? referrer
          : isSav3Ref
            ? referrer.toUpperCase()
            : referrer
                .split("-")
                .map((name) => name.charAt(0).toUpperCase() + name.slice(1))
                .join(" "),
      };
      setValues(
        (prevValues) => ({
          ...prevValues,
          ...newValues,
        }),
        false,
      );
      dispatch(onUpdateFormData(newValues));
    } else {
      // Fixing bug that keeped referrer in form values even when reloading the page
      // When no referrer is found in the URL, we reset the referrer field
      // This is done only when the user is in the first step, if not, the referrer is kept
      // Once per boot, and only after the persisted values are in: later visits
      // to step 1 (e.g. going back) keep values.
      if (step === 1 && !loading.formValues && !referrerResetRef.current) {
        referrerResetRef.current = true;

        const valuesWithReferrerReseted = {
          referrer: "",
          referredBool: false,
        };

        setValues((prevValues) => ({
          ...prevValues,
          ...valuesWithReferrerReseted,
        }));
        dispatch(onUpdateFormData(valuesWithReferrerReseted));
      }
    }
  }, [referrer, loading.formValues, step]);

  // Add cuit and email to form values if found in the URL in unification onboardings
  useEffect(() => {
    return;
    if (!isDev) return; //TODO: Remove this before merge prod OB UNIFIED and fix the bug
    if (emailByUrl && cuitByUrl && isUnifiedOnboarding()) {
      let newValues;

      if (isJuridicalOnboarding(template.onboardingType)) {
        newValues = {
          email: emailByUrl,
          businessInformation: {
            ...(values.businessInformation || {}),
            cuit: cuitByUrl,
          },
        };
      } else {
        newValues = {
          email: emailByUrl,
          cuit: cuitByUrl,
        };
      }

      setValues(
        (prevValues) => ({
          ...prevValues,
          ...newValues,
        }),
        false,
      );
      dispatch(onUpdateFormData(newValues));
    } else {
      // Fixing bug that keeped referrer in form values even when reloading the page
      // When no referrer is found in the URL, we reset the referrer field
      // This is done only when the user is in the first step, if not, the referrer is kept
      if (step === 1) {
        const valuesReseted = {
          cuit: "",
          email: "",
        };

        setValues((prevValues) => ({
          ...prevValues,
          ...valuesReseted,
        }));
        dispatch(onUpdateFormData(valuesReseted));
      }
    }
  }, [emailByUrl, cuitByUrl, loading.formValues, step]);

  // useEffect(() => {
  //   if (emailByUrl) {
  //     const newValues = {
  //       email: emailByUrl,
  //     };
  //     setValues(
  //       (prevValues) => ({
  //         ...prevValues,
  //         ...newValues,
  //       }),
  //       false
  //     );
  //     dispatch(onUpdateFormData(newValues));
  //   } else {
  //     // Fixing bug that keeped email in form values even when reloading the page
  //     // When no email is found in the URL, we reset the email field
  //     // This is done only when the user is in the first step, if not, the email is kept
  //     if (step === 1) {
  //       const valuesWithEmailReseted = {
  //         email: "",
  //       };

  //       setValues((prevValues) => ({
  //         ...prevValues,
  //         ...valuesWithEmailReseted,
  //       }));
  //       dispatch(onUpdateFormData(valuesWithEmailReseted));
  //     }
  //   }
  // }, [emailByUrl, loading.formValues, step]);

  const templateContainerStyle = () => {
    if (currentStepData?.supplementaryComponentInfo?.position === "top") {
      return `${templateContainer} ${flexColumn}`;
    }
    return `${templateContainer} `;
  };

  const secondaryFormData = isComponentStep(currentStepData)
    ? currentStepData.secondaryForm
    : undefined;

  const hasSecondaryFormCard = !!secondaryFormData && !secondaryFormData.inline;

  const isSecondaryFormOpen =
    isMobile && !!secondaryFormData && !!state.collectionData.isFormOpen;

  const hideCollectionColumn = isSecondaryFormOpen && hasSecondaryFormCard;

  const hasVisibleLayoutButtons =
    !!primaryButtonData?.show ||
    !!secondaryButtonData?.show ||
    !!tertiaryButtonData?.show;

  const secondaryFormElement = secondaryFormData && (
    <TemplateInterpreterSecondaryForm
      key={
        (state.collectionData?.activeIndex ?? 0) +
        "-" +
        (state.onboardingData?.formValues?.shareholders?.[
          (state.collectionData?.activeIndex ?? 1) - 1
        ]?.personType || "")
      }
      {...secondaryFormData}
      primaryForm={formik}
    />
  );

  useEffect(() => {
    dispatch(onUpdateIsNavigating(false));
  }, [step, subStep, securities.stepData.step, securities.stepData.subStep]);

  const { expiredSessionResume } = state.onboardingData;

  useEffect(() => {
    if (!expiredSessionResume || isLoading || typeof step !== "number") return;

    const livenessStep = template.steps?.find(({ subSteps }) =>
      subSteps?.some(({ label }) => label === "livenessValidation"),
    );
    const livenessSubStep = livenessStep?.subSteps?.find(
      ({ label }) => label === "livenessValidation",
    );
    if (!livenessStep || !livenessSubStep) {
      dispatch(onUpdateExpiredSessionResume(null));
      return;
    }

    const livenessStepNumber = Number(livenessStep.step);
    const livenessSubStepNumber = Number(livenessSubStep.step);
    if (step === livenessStepNumber && subStep === livenessSubStepNumber)
      return;

    dispatch(
      onUpdateExpiredSessionResume({ ...expiredSessionResume, step, subStep }),
    );
    dispatch(onUpdateStep(livenessStepNumber));
    dispatch(onUpdateSubStep(livenessSubStepNumber));
  }, [expiredSessionResume, isLoading, template.steps, step, subStep]);

  useEffect(() => {
    // Templates that don't declare a theme fallback to ALLARIA theme.
    if (template.steps && !template.theme) dispatch(onUpdateTheme("ALLARIA"));
  }, [template]);

  return (
    <Layout
      hideStepper={
        !!currentStep?.hideStepper ||
        (isMobile && !!currentStep?.hideStepperOnMobile)
      }
      hideMobileHeader={!!currentStep?.hideHeaderOnMobile}
      // Until its template loads, Allaria Digital would show the default
      // layout's logo and support button.
      hideLogo={template.hideLogo || isDigital}
      hideSupportButton={template.hideSupportButton || isDigital}
      centeredLogo={!!currentStep?.centeredLogo}
      isLoading={isLoading}
    >
      {isLoading ? (
        <div className={loadingStyle}>
          <LoadingCircle />
        </div>
      ) : (
        <>
          <EditDNIModal formik={formik} />
          <TramitNumberModal />
          <PEPModal />
          <QualifiedInvestorModal />
          <RiskProfileModal />
          <AccountObjectiveModal language={language} />
          <RiskProfileModalSecurities language={language} />
          <div className={templateContainerStyle()}>
            {currentStepData?.supplementaryComponentInfo?.position ===
              "top" && (
              <SupplementaryComponentInfoRenderer
                supplementaryComponentInfo={
                  currentStepData?.supplementaryComponentInfo
                }
                state={state}
              />
            )}

            {!hideCollectionColumn && (
              <Container className={` ${hasSecondaryFormCard ? form : ""}`}>
                {isComponentStep(currentStepData) &&
                currentStepData?.component &&
                !currentStepData.insideForm ? (
                  <>
                    {currentStepData.component(dispatch, state, formik)}
                    {currentStepData.stepModals}
                  </>
                ) : (
                  <FormContainer
                    currentStep={currentStep}
                    currentSubStep={
                      template.onboardingType === "SECURITIES"
                        ? Number(securities.stepData.subStep)
                        : Number(subStep)
                    }
                    hideFormHeader={
                      isSecondaryFormOpen ||
                      (isMobile &&
                        (!!currentStep?.hideFormHeaderOnMobile ||
                          !!currentStepData?.hideFormHeaderOnMobile)) ||
                      (typeof currentStepData?.hideFormHeader === "function"
                        ? currentStepData.hideFormHeader(state)
                        : !!currentStepData?.hideFormHeader)
                    }
                    className={`$${
                      !!isComponentStep(currentStepData) &&
                      currentStepData?.component
                        ? component
                        : ""
                    }`}
                    classNameFormContainer={`${
                      hasSecondaryFormCard
                        ? secondaryFormTemplate
                        : headerFormContainer
                    }`}
                  >
                    {isComponentStep(currentStepData) &&
                      currentStepData?.component &&
                      currentStepData.component(dispatch, state, formik)}
                    {isComponentStep(currentStepData) &&
                      currentStepData.stepModals}

                    {secondaryFormData?.inline &&
                      (!isMobile || isSecondaryFormOpen) &&
                      secondaryFormElement}

                    {initialValues &&
                      isBasicStep(currentStepData) &&
                      currentStepData?.fields && (
                        <TemplateInterpreterForm
                          onSubmit={handleSubmit}
                          templateLoaded={!!template}
                          currentStepData={currentStepData}
                          formik={formik}
                        />
                      )}
                  </FormContainer>
                )}
              </Container>
            )}
            {hasSecondaryFormCard &&
              (!isMobile || isSecondaryFormOpen) &&
              secondaryFormElement}
          </div>
          {currentStepData?.supplementaryComponentInfo?.position ===
            "bottom" && (
            <SupplementaryComponentInfoRenderer
              supplementaryComponentInfo={
                currentStepData?.supplementaryComponentInfo
              }
              state={state}
            />
          )}

          {!currentStepData?.hideLayoutButtons && hasVisibleLayoutButtons && (
            <ButtonGroup
              container={!isCrypto || isCryptoMobile}
              align={isCrypto ? "center" : "right"}
              disableMobileLayout={isCrypto && !isCryptoMobile}
              containerClassName={`${buttonsGroup} ${isCrypto && !isCryptoMobile ? cryptoDesktopButtons : ""}`}
              primary={
                primaryButtonData?.show
                  ? {
                      text: primaryButtonData.text,
                      type: primaryButtonData.type,
                      onClick: primaryButtonData.onClick,
                      isLoading: primaryButtonData.isLoading || isNavigating,
                      isDisabled: primaryButtonData.disabled || isNavigating,
                    }
                  : undefined
              }
              secondary={
                secondaryButtonData?.show
                  ? {
                      text: secondaryButtonData.text,
                      type: secondaryButtonData.type,
                      onClick: secondaryButtonData.onClick,
                      isLoading: secondaryButtonData.isLoading || isNavigating,
                      isDisabled: secondaryButtonData.disabled || isNavigating,
                    }
                  : undefined
              }
              tertiary={
                tertiaryButtonData?.show
                  ? {
                      // Matter's ButtonGroup spreads these over its own
                      // "tertiary" variant, so a variant here wins.
                      ...(tertiaryButtonData.variant && {
                        variant: tertiaryButtonData.variant,
                      }),
                      text: tertiaryButtonData.text,
                      type: tertiaryButtonData.type,
                      onClick: tertiaryButtonData.onClick,
                      isLoading: tertiaryButtonData.isLoading || isNavigating,
                      isDisabled: tertiaryButtonData.disabled || isNavigating,
                    }
                  : undefined
              }
            />
          )}
        </>
      )}
    </Layout>
  );
};

export default TemplateInterpreter;
